import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, filter, Observable, switchMap, takeWhile, tap, throwError, timer } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { TablePayload } from '../models/users';

interface ExportRequestResult {
  id: string;
}

type Status = 'Pending' | 'Processing' | 'Completed' | 'Failed';

interface ExportStatus {
  status: Status;
  progressPercentage: number;
}

interface ExportPayload extends Partial<TablePayload> {
  userIds: number[];
}

@Injectable({
  providedIn: 'root'
})
export class UsersExportService {
  private http = inject(HttpClient);
  private exportProgress = new BehaviorSubject(0);

  exportExcel({ search, sort }: Partial<TablePayload>, userIds: number[]): Observable<Blob> {
    const retryInterval = 3000;
    const body: ExportPayload = {
      search: search || null,
      sort: sort || null,
      userIds
    };
    const pendingStatuses: Partial<Status>[] = ['Pending', 'Processing'];
    const completedStatuses: Partial<Status>[] = ['Completed', 'Failed'];

    return this.http.post<ExportRequestResult>(`${environment.apiUrl}/users/export`, body).pipe(
      switchMap(({ id }) =>
        timer(0, retryInterval).pipe(
          switchMap(() => this.http.get<ExportStatus>(`${environment.apiUrl}/users/export/${id}`)),
          takeWhile(({ status }) => pendingStatuses.includes(status), true),
          tap(({ progressPercentage }) => this.exportProgress.next(progressPercentage)),
          filter(({ status }) => completedStatuses.includes(status)),
          switchMap(({ status }) => {
            this.exportProgress.next(0);
            if (status === 'Failed') {
              return throwError(() => new Error('Export failed'));
            } else {
              return this.http.get(`${environment.apiUrl}/users/export/${id}/file`, { responseType: 'blob' });
            }
          })
        )
      )
    );
  }

  getExportProgress() {
    return this.exportProgress.asObservable();
  }
}
